<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
  summary: {
    type: Object,
    default: () => ({ sent: 0, failed: 0, processed: 0 }),
  },
  contacts: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const campaign = computed(() => props.campaign || null);

const metrics = computed(() => ({
  sent: props.summary?.sent ?? 0,
  failed: props.summary?.failed ?? 0,
  processed: props.summary?.processed ?? 0,
}));

const summaryItems = computed(() => [
  {
    key: 'sent',
    label: t('CAMPAIGN.REPORT.SUMMARY.SENT'),
    value: metrics.value.sent,
    class: 'text-n-teal-11',
  },
  {
    key: 'failed',
    label: t('CAMPAIGN.REPORT.SUMMARY.FAILED'),
    value: metrics.value.failed,
    class: 'text-n-ruby-11',
  },
  {
    key: 'processed',
    label: t('CAMPAIGN.REPORT.SUMMARY.PROCESSED'),
    value: metrics.value.processed,
    class: 'text-n-slate-12',
  },
]);

const allContacts = computed(() => props.contacts || []);

const processedContactsCount = computed(() => allContacts.value.length);

const searchTerm = ref('');
const selectedStatus = ref('all');
const currentPage = ref(1);

const ITEMS_PER_PAGE = 50;

const normalize = value => (value || '').toString().toLowerCase();

const filteredContacts = computed(() => {
  const list = allContacts.value;
  const search = normalize(searchTerm.value.trim());
  const status = normalize(selectedStatus.value);

  return list.filter(contact => {
    const contactStatus = normalize(contact.status);
    const isStatusMatch = status === 'all' || contactStatus === status;

    if (!isStatusMatch) return false;
    if (!search) return true;

    const name = normalize(contact.name);
    const phone = normalize(contact.phone_number);

    return name.includes(search) || phone.includes(search);
  });
});

const filteredContactsCount = computed(() => filteredContacts.value.length);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(filteredContactsCount.value / ITEMS_PER_PAGE))
);
const paginatedContacts = computed(() => {
  if (!filteredContacts.value.length) return [];
  const startIndex = (currentPage.value - 1) * ITEMS_PER_PAGE;
  return filteredContacts.value.slice(startIndex, startIndex + ITEMS_PER_PAGE);
});
const showPaginationFooter = computed(
  () => filteredContactsCount.value > ITEMS_PER_PAGE
);

const subtitle = computed(() =>
  [
    t('CAMPAIGN.REPORT.CONTACT_COUNT', { count: processedContactsCount.value }),
    t('CAMPAIGN.REPORT.FILTER.RESULT_COUNT', {
      count: filteredContactsCount.value,
    }),
  ].join(' • ')
);

const hasContacts = computed(() => processedContactsCount.value > 0);
const hasFilteredContacts = computed(() => filteredContactsCount.value > 0);

const normalizeColor = color => {
  if (!color) return '';
  const trimmed = color.trim();
  if (!trimmed) return '';
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
};

const hexToRgb = color => {
  const hex = (color || '').replace('#', '').trim();
  if (!hex) return null;

  const expanded =
    hex.length === 3
      ? hex
          .split('')
          .map(char => char + char)
          .join('')
      : hex.padEnd(6, '0');

  const parseChannel = (start, end) => {
    const segment = expanded.slice(start, end);
    const value = parseInt(segment, 16);
    return Number.isNaN(value) ? null : value;
  };

  const r = parseChannel(0, 2);
  const g = parseChannel(2, 4);
  const b = parseChannel(4, 6);

  if ([r, g, b].some(channel => channel === null)) return null;

  return { r, g, b };
};

const getReadableTextColor = color => {
  const rgb = hexToRgb(color);
  if (!rgb) return 'var(--n-slate-12)';

  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(channel => {
    const norm = channel / 255;
    return norm <= 0.03928 ? norm / 12.92 : ((norm + 0.055) / 1.055) ** 2.4;
  });

  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  return luminance > 0.5 ? 'var(--n-slate-12)' : 'var(--n-alpha-1)';
};

const labelColorConfig = label => {
  const backgroundColor = normalizeColor(label?.color);

  if (!backgroundColor) {
    return {
      backgroundColor: 'var(--n-alpha-2)',
      borderColor: 'var(--n-weak)',
      textColor: 'var(--n-slate-12)',
      dotColor: 'var(--n-slate-10)',
    };
  }

  return {
    backgroundColor,
    borderColor: backgroundColor,
    textColor: getReadableTextColor(backgroundColor),
    dotColor: backgroundColor,
  };
};

const labelStyle = label => {
  const { backgroundColor, borderColor, textColor } = labelColorConfig(label);
  return {
    backgroundColor,
    borderColor,
    color: textColor,
  };
};

const labelDotStyle = label => {
  const { dotColor } = labelColorConfig(label);
  return { backgroundColor: dotColor };
};

const labelTextStyle = () => ({ color: '#FFFFFF' });

const getStatusLabel = status =>
  status === 'failed'
    ? t('CAMPAIGN.REPORT.STATUS.FAILED')
    : t('CAMPAIGN.REPORT.STATUS.SENT');

const statusClass = status =>
  status === 'failed' ? 'text-n-ruby-11' : 'text-n-teal-11';

const contactKey = contact =>
  contact?.id
    ? `contact-${contact.id}`
    : `fallback-${contact?.phone_number || contact?.name || contact?.status}`;

const handleClose = () => emit('close');

const slugify = value =>
  value
    ?.toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'campaign-report';

const csvFileName = computed(() => {
  const title = campaign.value?.title || t('CAMPAIGN.REPORT.UNTITLED');
  const slug = slugify(title);
  return `${slug}.csv`;
});

const escapeCsv = value => {
  const stringValue = value == null ? '' : String(value);
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const downloadCsv = () => {
  if (!hasContacts.value) return;

  const rows = [
    ['Name', 'Phone', 'Labels', 'Status'].map(escapeCsv).join(','),
    ...allContacts.value.map(contact => {
      const labels = (contact.labels || [])
        .map(label => label?.title)
        .filter(Boolean)
        .join(' | ');

      return [
        contact.name || '',
        contact.phone_number || '',
        labels,
        getStatusLabel(contact.status),
      ]
        .map(escapeCsv)
        .join(',');
    }),
  ];

  const blob = new Blob([rows.join('\n')], {
    type: 'text/csv;charset=utf-8;',
  });

  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute('download', csvFileName.value);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const placeholder = computed(() => t('CAMPAIGN.REPORT.PLACEHOLDER'));

const handlePageChange = page => {
  currentPage.value = page;
};

watch([searchTerm, selectedStatus], () => {
  currentPage.value = 1;
});

watch(filteredContactsCount, () => {
  if (currentPage.value > totalPages.value) {
    currentPage.value = totalPages.value;
  }
});

watch(allContacts, () => {
  currentPage.value = 1;
});
</script>

<template>
  <section class="mt-6 border border-n-weak rounded-2xl bg-n-solid-1 shadow-sm">
    <div
      class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 px-6 py-5 border-b border-n-weak"
    >
      <div class="flex flex-col gap-1">
        <h3 class="text-lg font-medium text-n-slate-12">
          {{ campaign?.title || t('CAMPAIGN.REPORT.UNTITLED') }}
        </h3>
        <p class="text-sm text-n-slate-10">{{ subtitle }}</p>
      </div>
      <div class="flex items-center gap-2">
        <Button
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-download"
          :disabled="!hasContacts"
          :aria-label="t('CAMPAIGN.REPORT.ACTIONS.DOWNLOAD')"
          @click="downloadCsv"
        >
          <span class="hidden sm:inline">{{
            t('CAMPAIGN.REPORT.ACTIONS.DOWNLOAD')
          }}</span>
        </Button>
        <Button
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-x"
          :aria-label="t('CAMPAIGN.REPORT.ACTIONS.CLOSE')"
          @click="handleClose"
        >
          <span class="hidden sm:inline">{{
            t('CAMPAIGN.REPORT.ACTIONS.CLOSE')
          }}</span>
        </Button>
      </div>
    </div>
    <div class="flex flex-col gap-6 px-6 py-5">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div
          v-for="item in summaryItems"
          :key="item.key"
          class="border border-n-weak rounded-xl bg-n-solid-2 px-4 py-3"
        >
          <span class="text-sm text-n-slate-10">{{ item.label }}</span>
          <p class="mt-1 text-2xl font-semibold" :class="item.class">
            {{ item.value }}
          </p>
        </div>
      </div>
      <div>
        <h4 class="mb-3 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.REPORT.TABLE.TITLE') }}
        </h4>
        <div class="border border-n-weak rounded-xl overflow-hidden">
          <div
            v-if="isLoading"
            class="flex items-center justify-center py-10 text-n-slate-11"
          >
            <Spinner />
          </div>
          <template v-else>
            <div v-if="!hasContacts" class="px-4 py-6 text-sm text-n-slate-11">
              {{ t('CAMPAIGN.REPORT.TABLE.EMPTY') }}
            </div>
            <div v-else>
              <div
                class="grid gap-3 px-4 pt-4 pb-2 border-b border-n-weak sm:grid-cols-[minmax(0,_1fr)_auto] sm:items-end"
              >
                <div class="flex flex-col gap-1 w-full">
                  <label
                    class="sr-only"
                    :for="`campaign-report-search-${campaign?.id || 'unknown'}`"
                  >
                    {{ t('CAMPAIGN.REPORT.FILTER.SEARCH_LABEL') }}
                  </label>
                  <input
                    :id="`campaign-report-search-${campaign?.id || 'unknown'}`"
                    v-model="searchTerm"
                    type="search"
                    :placeholder="
                      t('CAMPAIGN.REPORT.FILTER.SEARCH_PLACEHOLDER')
                    "
                    class="flex-1 px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-slate-7"
                  />
                </div>
                <div
                  class="flex flex-col gap-1 w-full sm:w-auto sm:min-w-[12rem]"
                >
                  <label
                    class="text-xs font-medium text-n-slate-10"
                    :for="`campaign-report-status-${campaign?.id || 'unknown'}`"
                  >
                    {{ t('CAMPAIGN.REPORT.FILTER.STATUS_LABEL') }}
                  </label>
                  <select
                    :id="`campaign-report-status-${campaign?.id || 'unknown'}`"
                    v-model="selectedStatus"
                    class="px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-slate-7"
                  >
                    <option value="all">
                      {{ t('CAMPAIGN.REPORT.FILTER.STATUS_OPTIONS.ALL') }}
                    </option>
                    <option value="sent">
                      {{ t('CAMPAIGN.REPORT.FILTER.STATUS_OPTIONS.SENT') }}
                    </option>
                    <option value="failed">
                      {{ t('CAMPAIGN.REPORT.FILTER.STATUS_OPTIONS.FAILED') }}
                    </option>
                  </select>
                </div>
              </div>
              <div
                v-if="!hasFilteredContacts"
                class="px-4 py-6 text-sm text-n-slate-11"
              >
                {{ t('CAMPAIGN.REPORT.FILTER.NO_MATCH') }}
              </div>
              <div v-else>
                <div class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-n-weak text-sm">
                    <thead class="bg-n-solid-2 text-left text-n-slate-10">
                      <tr>
                        <th scope="col" class="px-4 py-3 font-medium">
                          {{ t('CAMPAIGN.REPORT.TABLE.HEADERS.NAME') }}
                        </th>
                        <th scope="col" class="px-4 py-3 font-medium">
                          {{ t('CAMPAIGN.REPORT.TABLE.HEADERS.PHONE') }}
                        </th>
                        <th scope="col" class="px-4 py-3 font-medium">
                          {{ t('CAMPAIGN.REPORT.TABLE.HEADERS.LABELS') }}
                        </th>
                        <th scope="col" class="px-4 py-3 font-medium">
                          {{ t('CAMPAIGN.REPORT.TABLE.HEADERS.STATUS') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody
                      class="divide-y divide-n-weak bg-n-background text-n-slate-12"
                    >
                      <tr
                        v-for="contact in paginatedContacts"
                        :key="contactKey(contact)"
                      >
                        <td class="px-4 py-3 align-top">
                          <span
                            class="block text-sm font-medium text-n-slate-12"
                          >
                            {{ contact.name || placeholder }}
                          </span>
                        </td>
                        <td class="px-4 py-3 align-top text-sm text-n-slate-11">
                          {{ contact.phone_number || placeholder }}
                        </td>
                        <td class="px-4 py-3 align-top text-sm text-n-slate-11">
                          <div
                            v-if="contact.labels && contact.labels.length"
                            class="flex flex-wrap gap-2"
                          >
                            <span
                              v-for="label in contact.labels"
                              :key="label.id || label.title"
                              class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border"
                              :style="labelStyle(label)"
                            >
                              <span
                                class="flex-shrink-0 rounded-sm ring-1 ring-n-alpha-1 ring-inset w-1.5 h-1.5"
                                :style="labelDotStyle(label)"
                              />
                              <span :style="labelTextStyle(label)">{{
                                label.title
                              }}</span>
                            </span>
                          </div>
                          <span v-else class="text-n-slate-9">{{
                            placeholder
                          }}</span>
                        </td>
                        <td class="px-4 py-3 align-top">
                          <span
                            class="inline-flex items-center rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs font-medium"
                            :class="statusClass(contact.status)"
                          >
                            {{ getStatusLabel(contact.status) }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div
                  v-if="showPaginationFooter"
                  class="flex justify-end border-t border-n-weak bg-n-solid-2 px-4 py-3"
                >
                  <PaginationFooter
                    :current-page="currentPage"
                    :total-items="filteredContactsCount"
                    :items-per-page="ITEMS_PER_PAGE"
                    @update:current-page="handlePageChange"
                  />
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>
